import { Route, Redirect, BrowserRouter as Router } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { NotificationContainer } from 'react-notifications';
import Intercom from '@intercom/messenger-js-sdk';
import userStore from './Stores/User';
import { StripeProvider } from './StripeProvider';
import { observer } from 'mobx-react';
import OneGiftLogoSquare from './Components/OneGiftLogoSquare';

import Bye from './Pages/Bye';
import Share from './Pages/Share';
import About from './Pages/About';
import Contact from './Pages/Contact';
import Signin from './Pages/Signin';
import PreferredVoices from './Pages/PreferredVoices';
import Newsletter from './Pages/Newsletter';
import Embed from './Pages/Embed';
import history from './history';
import PaymentSuccess from './Pages/PaymentSuccess';
import Onboarding from './Pages/Onboarding';
import Drawabox from './Pages/Drawabox';
import SetupNew from './Pages/SetupNew';
import InstantAudioPreview from './Pages/SetupNew/Components/InstantAudioPreview';
import Ios from './Pages/SetupNew/Components/Ios';
import Android from './Pages/SetupNew/Components/Android';
import Trial from './Pages/Onboarding/Pages/Trial';
import Welcome from './Pages/Onboarding/Pages/Welcome';
import CreateAccount from './Pages/Onboarding/Pages/CreateAccount';
import HowToClip from './Pages/Onboarding/Pages/HowToClip';
import Complete from './Pages/Onboarding/Pages/Complete';
import TrialMobile from './Pages/Onboarding/Pages/TrialMobile';
import ChooseVoice from './Pages/Onboarding/Pages/ChooseVoice';
import PodcastApp from './Pages/Onboarding/Pages/PodcastApp';
import ChoosePlan from './Pages/Onboarding/Pages/ChoosePlan';
import Types from './Pages/Onboarding/Pages/Types';
import Quickstart from './Pages/Onboarding/Pages/Quickstart';
import Quickstart1 from './Pages/Onboarding/Pages/Quickstart1';
import Quickstart2 from './Pages/Onboarding/Pages/Quickstart2';
import Quickstart3 from './Pages/Onboarding/Pages/Quickstart3';
import ContentSwitch from './Pages/ContentSwitch';
import SocialThumbnailTemplate from './Pages/SocialThumbnailTemplate';
import Subscribe from './Pages/Subscribe';
import EditText from './Pages/EditText';
import Upload from './Pages/DiscoverNew/Components/Upload';
import LinkEmail from './Pages/LinkEmail';
import Account from './Pages/Account';
import Shortcuts from './Pages/Shortcuts';
import InstallAndroid from './Pages/InstallAndroid';
import CheckoutForm from './Pages/CheckoutForm';
import TermsOfService from './Pages/TermsOfService';
import PrivacyPolicy from './Pages/PrivacyPolicy';
import DmcaPolicy from './Pages/DmcaPolicy';
import CookieConsent from './Components/CookieConsent';
import CookieManager from './Components/CookieManager';
import FeedLanding from './Pages/FeedLanding';
import FreeAiTextToSpeech from './Pages/Landing/FreeAiTextToSpeech';
import FeedsDirectory from './Pages/FeedsDirectory';

const AppStatus = () => {
  const isLocal = window.location.href.includes('http://localhost') || window.location.href.includes('http://192.168.1.66');
  const isStaging = window.location.href.includes('https://dev.audiblogs.com') || window.location.href.includes('https://dev.audioread.com');
  if (isLocal || isStaging) {
    return (
      <div
        style={{
          backgroundColor: isLocal ? 'red' : 'orange',
          color: 'white',
          textAlign: 'center',
          padding: '5px',
          fontSize: '14px',
          fontWeight: 'bold',
          position: 'fixed',
          top: 0,
          width: '100%',
          zIndex: 1001,
        }}
      >
        {isLocal ? 'Local' : 'Staging'}
      </div>
    );
  }
  return null;
};

const App = observer(() => {
  const [isCookieManagerOpen, setIsCookieManagerOpen] = useState(false);
  const { user } = userStore;

  useEffect(() => {
    // Initialize Intercom with hidden launcher - important to start with it hidden
    Intercom({
      app_id: "fduylg2r",
      hide_default_launcher: true
    });

    // Add global function to open cookie manager
    window.openCookieManager = () => setIsCookieManagerOpen(true);
    
    // Function to reposition the Intercom launcher above the player
    const adjustIntercomPosition = () => {
      // Directly use Intercom's API to set custom launcher attributes
      // This approach is much more reliable than trying to manipulate the DOM
      // Give it a delay to ensure Intercom is fully initialized
      setTimeout(() => {
        if (window.Intercom) {
          try {
            window.Intercom('update', {
              // Hide the default launcher
              hide_default_launcher: true
            });
            
            // Now use messenger API to show a custom launcher in a better position
            window.Intercom('onShow', () => {
              const customLauncher = document.querySelector('.intercom-launcher');
              if (customLauncher) {
                customLauncher.style.bottom = 'unset';
                customLauncher.style.top = 'calc(100vh - 250px)';
              }
            });
            
            // Create a custom launcher (optional)
            const customStyle = document.createElement('style');
            customStyle.innerHTML = `
              .custom-intercom-launcher {
                position: fixed;
                right: 20px;
                width: 60px;
                height: 60px;
                border-radius: 50%;
                background-color: #0071b2;
                box-shadow: 0 4px 8px rgba(0,0,0,0.3);
                cursor: pointer;
                z-index: 2000;
                display: flex;
                align-items: center;
                justify-content: center;
                background-image: url('/favicon.ico');
                background-size: 30px;
                background-position: center;
                background-repeat: no-repeat;
                /* Default position for desktop */
                bottom: 110px;
              }
              
              /* Mobile position adjustments */
              @media (max-width: 1199.98px) {
                .custom-intercom-launcher {
                  bottom: 170px; /* Higher position on mobile to avoid player + menu */
                }
              }
            `;
            document.head.appendChild(customStyle);
            
            // Create a custom launcher element
            const launcher = document.createElement('div');
            launcher.className = 'custom-intercom-launcher';
            launcher.onclick = () => window.Intercom('show');
            document.body.appendChild(launcher);
          } catch (e) {
            console.error("Error setting up custom Intercom launcher:", e);
          }
        }
      }, 2000);
    };
    
    adjustIntercomPosition();
    
    return () => {
      // Clean up custom launcher on unmount
      const customLauncher = document.querySelector('.custom-intercom-launcher');
      if (customLauncher) {
        document.body.removeChild(customLauncher);
      }
    };
  }, []);

  return (
    <StripeProvider>
      <AppStatus />
      {user && (
        <div style={{ 
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1001 // Higher than AppStatus
        }}>
          <OneGiftLogoSquare />
        </div>
      )}
      <Router history={history}>
        <Route path="/" exact component={ContentSwitch} />
        <Route path="/playlist" exact component={ContentSwitch} />
        <Route path="/add" exact component={ContentSwitch} />
        <Route path="/search" exact component={ContentSwitch} />
        <Route path="/settings" exact component={ContentSwitch} />
        <Route path="/discover" exact component={ContentSwitch} />
        <Route path="/feeds" exact component={FeedsDirectory} />
        <Route path="/PreferredVoices" exact component={PreferredVoices} />
        <Route path="/about" exact component={About} />
        <Route path="/contact" exact component={Contact} />
        <Route path="/bye/" exact component={Bye} />
        <Route path="/player/:USER_ID" component={Signin} />
        <Route path="/share/:filename" component={Share} />
        <Route path="/setup/" exact>
          <Redirect to="/onboarding/quickstart/2" />
        </Route>
        <Route path="/Account" exact component={Account} />

        <Route path="/signin/" exact component={Signin} />
        <Route path="/finishSignin" component={Signin} />
        <Route path="/signin/:USER_ID" component={Signin} />
        <Route path="/newsletter" exact component={Newsletter} />
        <Route path="/embed" exact component={Embed} />
        <Route path="/payment/success" exact component={PaymentSuccess} />
        <Route path="/onboarding" exact component={Onboarding} />
        <Route path="/setupnew/trial">
          <Redirect to="/onboarding/trial" />
        </Route>
        <Route path="/onboarding/trial" exact component={Trial} />
        <Route path="/onboarding/TrialMobile" exact component={TrialMobile} />
        <Route path="/onboarding/Welcome" exact component={Welcome} />
        <Route path="/onboarding/CreateAccount" exact component={CreateAccount} />
        <Route path="/onboarding/HowToClip" exact component={HowToClip} />
        <Route path="/onboarding/Complete" exact component={Complete} />
        <Route path="/onboarding/ChooseVoice" exact component={ChooseVoice} />
        <Route path="/onboarding/ChoosePlan" exact component={ChoosePlan} />
        <Route path="/onboarding/PodcastApp" exact component={PodcastApp} />
        <Route path="/onboarding/Types" exact component={Types} />
        <Route path="/onboarding/Quickstart" exact component={Quickstart} />
        <Route path="/onboarding/Quickstart/2" exact component={Quickstart2} />
        <Route path="/onboarding/Quickstart/1" exact component={Quickstart1} />
        <Route path="/onboarding/Quickstart/3" exact component={Quickstart3} />

        <Route path="/setupnew" exact component={Quickstart2} />
        <Route path="/demo" exact component={InstantAudioPreview} />
        <Route path="/setupnew/ios" exact component={Quickstart2} />
        <Route path="/setupnew/android" exact component={Quickstart2} />
        <Route path="/subscribe" exact component={Subscribe} />
        <Route path="/subscription" exact component={Subscribe} />
        <Route path="/add/text" exact component={EditText} />
        <Route path="/add/file" exact component={Upload} />
        <Route path="/link" exact component={LinkEmail} />
        <Route path="/drawabox" exact component={Drawabox} />
        <Route path="/onboarding/ios" exact component={Shortcuts} />
        <Route path="/onboarding/android" exact component={InstallAndroid} />
        <Route path="/CheckoutForm" exact component={CheckoutForm} />
        <Route path="/terms" exact component={TermsOfService} />
        <Route path="/privacy" exact component={PrivacyPolicy} />
        <Route path="/dmca" exact component={DmcaPolicy} />
        
        <Route
          path="/SocialThumbnailTemplate"
          exact
          component={SocialThumbnailTemplate}
        />
        <Route path="/feed/:feedId/*" component={FeedLanding} />
        <Route path="/freeaitexttospeech" exact component={FreeAiTextToSpeech} />
      </Router>
      <NotificationContainer />
      <CookieConsent onManageClick={() => setIsCookieManagerOpen(true)} />
      <CookieManager
        isOpen={isCookieManagerOpen}
        onClose={() => setIsCookieManagerOpen(false)}
      />
    </StripeProvider>
  );
});

export default App;
